import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getStoredSession, logoutLocally } from "../../api/auth";
import {
  getTeacherWebComparisonRunStatus,
  getTeacherWebGlbDownloadUrl,
  getTeacherWebLlmStatus,
  TeacherWebComparisonRunStatus,
  TeacherWebCompData,
  TeacherWebGlbDownload,
  TeacherWebLlmAnalysis,
  TeacherWebQwenDescription,
} from "../../api/teacherWeb";
import { GlbPreview } from "../../three/GlbPreview";
import { loadGlbObjectUrlFromRemote } from "../../utils/glbFile";

type ModelSlot = "first" | "second";

type ChartDefinition = {
  description: string;
  key: string;
  name: string;
  shortName: string;
  unit: string;
};

type ChartItem = {
  definition: ChartDefinition;
  maxFrameCount: number;
  seriesFirst: number[];
  seriesSecond: number[];
};

const lastTeacherTokenKey = "pingpi.teacherWeb.lastTeacherToken";
const modelSlots: ModelSlot[] = ["first", "second"];

const chartDefinitions: ChartDefinition[] = [
  {
    key: "elbow_angle_right",
    name: "右肘部角度",
    shortName: "右肘角",
    unit: "度",
    description: "衡量手臂弯曲程度的角度，角度越小表示手臂弯曲越多，角度越大表示手臂越伸直。",
  },
  {
    key: "shoulder_hip_angle_right",
    name: "右肩髋角度",
    shortName: "右肩髋角",
    unit: "度",
    description: "反映大臂相对躯干的抬高程度，角度越大表示大臂抬得越高，角度越小表示大臂越贴近身体。",
  },
  {
    key: "shoulder_arm_angle_right",
    name: "右肩臂角度",
    shortName: "右肩臂角",
    unit: "度",
    description: "衡量大臂相对肩线的外展程度，角度越小表示手臂越贴近身体中线，角度越大表示手臂越向外展开。",
  },
  {
    key: "shoulder_rotation_right",
    name: "右肩部旋转角度",
    shortName: "右肩转角",
    unit: "度",
    description: "反映击球过程中肩部的转动幅度，正值表示肩向前转动，负值表示肩向后转动。",
  },
  {
    key: "hip_angle_right",
    name: "右髋部角度",
    shortName: "右髋角",
    unit: "度",
    description: "衡量髋关节的弯曲程度，角度越小表示身体越弯曲，角度越大表示身体越直立。",
  },
  {
    key: "hip_rotation_right",
    name: "右髋部旋转角度",
    shortName: "右髋转角",
    unit: "度",
    description: "反映击球过程中髋部的转动幅度，正值表示髋向前转动，负值表示髋向后转动。",
  },
  {
    key: "knee_angle_right",
    name: "右膝盖角度",
    shortName: "右膝角",
    unit: "度",
    description: "衡量膝盖弯曲程度的角度，角度越小表示下蹲越深，角度越大表示腿部越伸直。",
  },
  {
    key: "elbow_angle_left",
    name: "左肘部角度",
    shortName: "左肘角",
    unit: "度",
    description: "衡量手臂弯曲程度的角度，角度越小表示手臂弯曲越多，角度越大表示手臂越伸直。",
  },
  {
    key: "shoulder_hip_angle_left",
    name: "左肩髋角度",
    shortName: "左肩髋角",
    unit: "度",
    description: "反映大臂相对躯干的抬高程度，角度越大表示大臂抬得越高，角度越小表示大臂越贴近身体。",
  },
  {
    key: "shoulder_arm_angle_left",
    name: "左肩臂角度",
    shortName: "左肩臂角",
    unit: "度",
    description: "衡量大臂相对肩线的外展程度，角度越小表示手臂越贴近身体中线，角度越大表示手臂越向外展开。",
  },
  {
    key: "shoulder_rotation_left",
    name: "左肩部旋转角度",
    shortName: "左肩转角",
    unit: "度",
    description: "反映击球过程中肩部的转动幅度，正值表示肩向前转动，负值表示肩向后转动。",
  },
  {
    key: "hip_angle_left",
    name: "左髋部角度",
    shortName: "左髋角",
    unit: "度",
    description: "衡量髋关节的弯曲程度，角度越小表示身体越弯曲，角度越大表示身体越直立。",
  },
  {
    key: "hip_rotation_left",
    name: "左髋部旋转角度",
    shortName: "左髋转角",
    unit: "度",
    description: "反映击球过程中髋部的转动幅度，正值表示髋向前转动，负值表示髋向后转动。",
  },
  {
    key: "knee_angle_left",
    name: "左膝盖角度",
    shortName: "左膝角",
    unit: "度",
    description: "衡量膝盖弯曲程度的角度，角度越小表示下蹲越深，角度越大表示腿部越伸直。",
  },
  {
    key: "trunk_flexion_angle",
    name: "躯干前屈角度",
    shortName: "躯干前屈",
    unit: "度",
    description: "衡量躯干前后方向的倾斜程度，可用于观察身体是否过度前倾或后仰。",
  },
  {
    key: "trunk_lateral_tilt_angle",
    name: "躯干侧倾角度",
    shortName: "躯干侧倾",
    unit: "度",
    description: "衡量躯干左右方向的倾斜程度，可用于观察击球过程中身体重心是否偏移。",
  },
  {
    key: "trunk_rotation_angle",
    name: "躯干旋转角度",
    shortName: "躯干旋转",
    unit: "度",
    description: "衡量击球过程中躯干的转动幅度，可用于观察转体发力是否充分。",
  },
  {
    key: "center_of_gravity",
    name: "重心高度",
    shortName: "重心高",
    unit: "pu",
    description: "衡量全身重心相对腿长的高度比值，数值越小表示重心越低，数值越大表示重心越高。",
  },
];

const techTypeLabels: Record<string, string> = {
  left_backhand_backspin: "左手反手拉下旋球",
  left_backhand_chop: "左手反手削球",
  left_backhand_defense: "左手反手防守",
  left_backhand_drive: "左手反手攻球",
  left_backhand_push: "左手反手搓球",
  left_backhand_topspin: "左手反手拉上旋球",
  left_forehand_backspin: "左手正手拉下旋球",
  left_forehand_chop: "左手正手削球",
  left_forehand_defense: "左手正手防守",
  left_forehand_drive: "左手正手攻球",
  left_forehand_push: "左手正手搓球",
  left_forehand_topspin: "左手正手拉上旋球",
  right_backhand_backspin: "右手反手拉下旋球",
  right_backhand_chop: "右手反手削球",
  right_backhand_defense: "右手反手防守",
  right_backhand_drive: "右手反手攻球",
  right_backhand_push: "右手反手搓球",
  right_backhand_topspin: "右手反手拉上旋球",
  right_forehand_backspin: "右手正手拉下旋球",
  right_forehand_chop: "右手正手削球",
  right_forehand_defense: "右手正手防守",
  right_forehand_drive: "右手正手攻球",
  right_forehand_push: "右手正手搓球",
  right_forehand_topspin: "右手正手拉上旋球",
};

export function ResultPage() {
  const navigate = useNavigate();
  const { runId } = useParams();
  const [runStatus, setRunStatus] = useState<TeacherWebComparisonRunStatus | null>(null);
  const [llmAnalysis, setLlmAnalysis] = useState<TeacherWebLlmAnalysis | null>(null);
  const [llmStatus, setLlmStatus] = useState<"idle" | "pending" | "completed" | "error">("idle");
  const [pageError, setPageError] = useState("");
  const [modelErrors, setModelErrors] = useState<Partial<Record<ModelSlot, string>>>({});
  const [modelReady, setModelReady] = useState<Partial<Record<ModelSlot, boolean>>>({});
  const [modelUrls, setModelUrls] = useState<Partial<Record<ModelSlot, string>>>({});
  const [modelDurations, setModelDurations] = useState<Partial<Record<ModelSlot, number>>>({});
  const [fullscreenSlot, setFullscreenSlot] = useState<ModelSlot | null>(null);
  const [showModelHelp, setShowModelHelp] = useState(false);
  const [showChartHelp, setShowChartHelp] = useState(false);
  const [selectedChartKey, setSelectedChartKey] = useState("");
  const [chartFrame, setChartFrame] = useState(0);
  const [isModelPlaying, setIsModelPlaying] = useState(false);
  const [modelPlaybackTime, setModelPlaybackTime] = useState(0);
  const modelObjectUrlCacheRef = useRef(new Map<string, string>());
  const modelDownloadPromiseCacheRef = useRef(new Map<string, Promise<string>>());
  const isDisposedRef = useRef(false);
  const currentUserId = getStoredSession()?.user.uid;

  useEffect(() => {
    if (!runId) {
      setPageError("缺少任务 ID");
      return;
    }

    const currentRunId = runId;
    const controller = new AbortController();
    let stopped = false;
    let timer: number | undefined;

    async function poll() {
      try {
        const status = await getTeacherWebComparisonRunStatus(currentRunId, controller.signal);
        if (stopped) return;
        setRunStatus(status);
        setPageError("");

        if (status.status !== "comp completed" && status.status !== "comp failed") {
          timer = window.setTimeout(poll, 3000);
        }
      } catch (error) {
        if (stopped || controller.signal.aborted) return;
        setPageError(error instanceof Error ? error.message : "结果读取失败");
        timer = window.setTimeout(poll, 5000);
      }
    }

    void poll();

    return () => {
      stopped = true;
      controller.abort();
      if (timer) window.clearTimeout(timer);
    };
  }, [runId]);

  useEffect(() => {
    if (!runId || runStatus?.status !== "comp completed") return;

    const currentRunId = runId;
    const controller = new AbortController();
    let stopped = false;
    let timer: number | undefined;

    async function pollLlm() {
      try {
        const status = await getTeacherWebLlmStatus(currentRunId, controller.signal);
        if (stopped) return;
        setLlmStatus(status.status);
        setLlmAnalysis(status.llm_analysis);

        if (status.status !== "completed") {
          timer = window.setTimeout(pollLlm, 5000);
        }
      } catch {
        if (stopped || controller.signal.aborted) return;
        setLlmStatus("error");
      }
    }

    void pollLlm();

    return () => {
      stopped = true;
      controller.abort();
      if (timer) window.clearTimeout(timer);
    };
  }, [runId, runStatus?.status]);

  const downloads = runStatus?.glb_downloads;
  const downloadSignature = `${downloads?.first?.objectKey ?? ""}|${downloads?.second?.objectKey ?? ""}`;

  useEffect(() => {
    if (!runId || runStatus?.status !== "comp completed" || !downloads) return;

    const controller = new AbortController();

    for (const slot of modelSlots) {
      const download = downloads[slot];
      const objectKey = download?.objectKey;
      if (!objectKey) continue;

      const cachedUrl = modelObjectUrlCacheRef.current.get(objectKey);
      if (cachedUrl) {
        setModelUrls((current) => ({ ...current, [slot]: cachedUrl }));
        continue;
      }

      let downloadPromise = modelDownloadPromiseCacheRef.current.get(objectKey);
      if (!downloadPromise) {
        downloadPromise = getTeacherWebGlbDownloadUrl(runId, slot, controller.signal).then((data) => {
          if (data.objectKey !== objectKey) {
            throw new Error("模型文件不匹配，请重试");
          }
          return loadGlbObjectUrlFromRemote(data.url, data.objectKey);
        });
        modelDownloadPromiseCacheRef.current.set(objectKey, downloadPromise);
      }

      setModelErrors((current) => ({ ...current, [slot]: "" }));

      downloadPromise
        .then((url) => {
          modelDownloadPromiseCacheRef.current.delete(objectKey);
          if (isDisposedRef.current) {
            URL.revokeObjectURL(url);
            return;
          }

          const existingUrl = modelObjectUrlCacheRef.current.get(objectKey);
          const nextUrl = existingUrl || url;
          if (existingUrl && existingUrl !== url) {
            URL.revokeObjectURL(url);
          } else {
            modelObjectUrlCacheRef.current.set(objectKey, url);
          }

          if (!controller.signal.aborted) {
            setModelUrls((current) => ({ ...current, [slot]: nextUrl }));
          }
        })
        .catch((error) => {
          modelDownloadPromiseCacheRef.current.delete(objectKey);
          if (!controller.signal.aborted) {
            setModelErrors((current) => ({
              ...current,
              [slot]: error instanceof Error ? error.message : "模型下载失败",
            }));
          }
        });
    }

    return () => controller.abort();
  }, [downloadSignature, downloads, runId, runStatus?.status]);

  useEffect(() => {
    isDisposedRef.current = false;
    return () => {
      isDisposedRef.current = true;
      modelDownloadPromiseCacheRef.current.clear();
      modelObjectUrlCacheRef.current.forEach((url) => URL.revokeObjectURL(url));
      modelObjectUrlCacheRef.current.clear();
    };
  }, []);

  const firstData = runStatus?.comp_results?.data?.[0] ?? null;
  const secondData = runStatus?.comp_results?.data?.[1] ?? null;
  const embeddedAnalysis = firstData?.qwen_analysis ?? null;
  const analysis = llmAnalysis ?? embeddedAnalysis;
  const techType = runStatus?.comp_results?.tech_type ?? undefined;
  const techLabel = techType ? techTypeLabels[techType] ?? techType : "AI教练分析";
  const maxModelDuration = Math.max(modelDurations.first ?? 0, modelDurations.second ?? 0);
  const isTeacherViewer = Boolean(currentUserId && runStatus?.teacher_user_id === currentUserId);

  const chartItems = useMemo(() => buildChartItems(firstData, secondData, preferredChartSide(techType)), [firstData, secondData, techType]);
  const selectedChart = chartItems.find((item) => item.definition.key === selectedChartKey) ?? chartItems[0] ?? null;

  useEffect(() => {
    if (chartItems.length === 0) {
      setSelectedChartKey("");
      return;
    }
    if (!chartItems.some((item) => item.definition.key === selectedChartKey)) {
      setSelectedChartKey(chartItems[0].definition.key);
      setChartFrame(0);
    }
  }, [chartItems, selectedChartKey]);

  if (!runId) {
    return <ResultErrorCard message="缺少任务 ID" />;
  }

  function handleLogout() {
    if (!window.confirm("确定要退出登录吗？")) return;
    logoutLocally();
    navigate("/compat", { replace: true });
  }

  function handleReselectVideo() {
    const token = getLastTeacherToken();
    if (token) {
      navigate(`/t/${token}`);
      return;
    }
    navigate(-1);
  }

  function handleBackToDashboard() {
    navigate("/teacher/dashboard");
  }

  function handleModelTimeUpdate(time: number) {
    if (!isModelPlaying) return;
    setModelPlaybackTime(time);
    if (selectedChart) {
      setChartFrame(frameFromProgress(time, maxModelDuration, selectedChart.maxFrameCount));
    }
  }

  function handleChartFrameChange(frame: number) {
    if (!selectedChart) return;
    const nextFrame = clamp(Math.round(frame), 0, Math.max(0, selectedChart.maxFrameCount - 1));
    setChartFrame(nextFrame);
    if (maxModelDuration > 0 && selectedChart.maxFrameCount > 1) {
      setModelPlaybackTime((nextFrame / (selectedChart.maxFrameCount - 1)) * maxModelDuration);
    }
  }

  return (
    <section className="result-page">
      <header className="result-mobile-header">
        <div className="upload-wizard-brand-row">
          <div className="student-login-brand" aria-label="乒派">
            <img src="/pingpi-logo.png" alt="" aria-hidden="true" />
            <span>乒派</span>
          </div>
          <div className="result-header-actions">
            {isTeacherViewer ? (
              <button className="result-text-button" type="button" onClick={handleBackToDashboard}>
                返回教师看板
              </button>
            ) : (
              <button className="result-text-button" type="button" onClick={handleReselectVideo}>
                重新选择视频
              </button>
            )}
            <button className="upload-logout-button" type="button" aria-label="退出登录" title="退出登录" onClick={handleLogout}>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M9 6.75V5.5A2.5 2.5 0 0 1 11.5 3h5A2.5 2.5 0 0 1 19 5.5v13a2.5 2.5 0 0 1-2.5 2.5h-5A2.5 2.5 0 0 1 9 18.5v-1.25" />
                <path d="M4 12h10" />
                <path d="m11 8 4 4-4 4" />
              </svg>
            </button>
          </div>
        </div>

        <div className="result-title-row">
          <div>
            <span className={`status-pill ${statusTone(runStatus?.status, pageError)}`}>{statusText(runStatus?.status, pageError)}</span>
            <h2>{techLabel}</h2>
          </div>
          <button className="round-help-button" type="button" aria-label="查看模型操作说明" onClick={() => setShowModelHelp(true)}>
            ?
          </button>
        </div>
      </header>

      {pageError ? <ResultErrorCard message={pageError} /> : null}

      <article className="result-card result-model-section">
        <div className="dual-model-grid">
          <ModelPanel
            bodyCenter={firstData?.body_center}
            directions={firstData?.directions}
            error={modelErrors.first}
            glbUrl={modelUrls.first ?? null}
            isPlaying={isModelPlaying}
            label="学生动作"
            playbackTime={modelPlaybackTime}
            slot="first"
            onDurationChange={(duration) => setModelDurations((current) => ({ ...current, first: duration }))}
            onError={(message) => setModelErrors((current) => ({ ...current, first: message }))}
            onFullscreen={() => setFullscreenSlot("first")}
            onReady={() => setModelReady((current) => ({ ...current, first: true }))}
            onTimeUpdate={handleModelTimeUpdate}
          />
          <ModelPanel
            bodyCenter={secondData?.body_center}
            directions={secondData?.directions}
            error={modelErrors.second}
            glbUrl={modelUrls.second ?? null}
            isPlaying={isModelPlaying}
            label="标准动作"
            playbackTime={modelPlaybackTime}
            slot="second"
            onDurationChange={(duration) => setModelDurations((current) => ({ ...current, second: duration }))}
            onError={(message) => setModelErrors((current) => ({ ...current, second: message }))}
            onFullscreen={() => setFullscreenSlot("second")}
            onReady={() => setModelReady((current) => ({ ...current, second: true }))}
            onTimeUpdate={undefined}
          />
        </div>
        <ModelPlaybackControl
          disabled={!modelReady.first && !modelReady.second}
          duration={maxModelDuration}
          isPlaying={isModelPlaying}
          time={modelPlaybackTime}
          onSeek={(time) => {
            setModelPlaybackTime(time);
            if (selectedChart) {
              setChartFrame(frameFromProgress(time, maxModelDuration, selectedChart.maxFrameCount));
            }
          }}
          onToggle={() => setIsModelPlaying((value) => !value)}
        />
      </article>

      <article className="result-card result-data-card">
        <div className="result-card-header compact">
          <div>
            <h3>动作数据</h3>
            {selectedChart ? <p>{selectedChart.definition.description}</p> : null}
          </div>
          {selectedChart ? (
            <button className="chart-help-button" type="button" onClick={() => setShowChartHelp(true)}>
              指标说明
            </button>
          ) : null}
        </div>
        <MotionChart
          item={selectedChart}
          selectedFrame={chartFrame}
          onChartChange={(key) => {
            setSelectedChartKey(key);
            setChartFrame(0);
          }}
          onFrameChange={handleChartFrameChange}
          options={chartItems}
        />
      </article>

      <article className="result-card result-report-card">
        <div className="result-card-header compact">
          <div>
            <h3>AI报告</h3>
          </div>
          <span className={`status-pill ${analysis ? "success" : llmStatus === "error" ? "error" : "running"}`}>
            {analysis ? "已生成" : llmStatus === "error" ? "待重试" : "生成中"}
          </span>
        </div>
        <LlmReport analysis={analysis} llmStatus={llmStatus} />
      </article>

      {showModelHelp ? (
        <InfoDialog title="3D模型操作" onClose={() => setShowModelHelp(false)}>
          <p>单指拖动可以旋转模型。</p>
          <p>双指可以放大、缩小或平移视角。</p>
        </InfoDialog>
      ) : null}

      {showChartHelp && selectedChart ? (
        <InfoDialog title={selectedChart.definition.name} onClose={() => setShowChartHelp(false)}>
          <p>{selectedChart.definition.description}</p>
        </InfoDialog>
      ) : null}

      {fullscreenSlot ? (
        <FullscreenModel
          bodyCenter={fullscreenSlot === "first" ? firstData?.body_center : secondData?.body_center}
          directions={fullscreenSlot === "first" ? firstData?.directions : secondData?.directions}
          glbUrl={modelUrls[fullscreenSlot] ?? null}
          isPlaying={isModelPlaying}
          label={fullscreenSlot === "first" ? "学生动作" : "标准动作"}
          playbackTime={modelPlaybackTime}
          onClose={() => setFullscreenSlot(null)}
          onDurationChange={(duration) => setModelDurations((current) => ({ ...current, [fullscreenSlot]: duration }))}
          onError={(message) => setModelErrors((current) => ({ ...current, [fullscreenSlot]: message }))}
          onReady={() => setModelReady((current) => ({ ...current, [fullscreenSlot]: true }))}
          onTimeUpdate={handleModelTimeUpdate}
        />
      ) : null}
    </section>
  );
}

function ModelPanel({
  bodyCenter,
  directions,
  error,
  glbUrl,
  isPlaying,
  label,
  playbackTime,
  onDurationChange,
  onError,
  onFullscreen,
  onReady,
  onTimeUpdate,
}: {
  bodyCenter?: number[];
  directions?: Record<string, number[]>;
  error?: string;
  glbUrl: string | null;
  isPlaying: boolean;
  label: string;
  playbackTime: number;
  slot: ModelSlot;
  onDurationChange: (duration: number) => void;
  onError: (message: string) => void;
  onFullscreen: () => void;
  onReady: () => void;
  onTimeUpdate?: (time: number) => void;
}) {
  return (
    <section className="model-panel">
      <div className="model-panel-title">
        <strong>{label}</strong>
        <button className="model-fullscreen-button" type="button" aria-label={`放大${label}`} onClick={onFullscreen}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M8 3H3v5" />
            <path d="M3 3l7 7" />
            <path d="M16 3h5v5" />
            <path d="M21 3l-7 7" />
            <path d="M8 21H3v-5" />
            <path d="M3 21l7-7" />
            <path d="M16 21h5v-5" />
            <path d="M21 21l-7-7" />
          </svg>
        </button>
      </div>
      <div className="result-model-stage compact">
        <GlbPreview
          bodyCenter={bodyCenter}
          directions={directions}
          emptyLabel={null}
          glbUrl={glbUrl}
          isPlaying={isPlaying}
          playbackTime={playbackTime}
          onDurationChange={onDurationChange}
          onError={onError}
          onReady={onReady}
          onTimeUpdate={onTimeUpdate}
        />
      </div>
      {error ? <p className="model-inline-error">{error}</p> : null}
    </section>
  );
}

function FullscreenModel({
  bodyCenter,
  directions,
  glbUrl,
  isPlaying,
  label,
  playbackTime,
  onClose,
  onDurationChange,
  onError,
  onReady,
  onTimeUpdate,
}: {
  bodyCenter?: number[];
  directions?: Record<string, number[]>;
  glbUrl: string | null;
  isPlaying: boolean;
  label: string;
  playbackTime: number;
  onClose: () => void;
  onDurationChange: (duration: number) => void;
  onError: (message: string) => void;
  onReady: () => void;
  onTimeUpdate?: (time: number) => void;
}) {
  return (
    <div className="fullscreen-model-backdrop">
      <div className="fullscreen-model">
        <header>
          <strong>{label}</strong>
          <button type="button" onClick={onClose}>关闭</button>
        </header>
        <GlbPreview
          bodyCenter={bodyCenter}
          directions={directions}
          emptyLabel={null}
          glbUrl={glbUrl}
          isPlaying={isPlaying}
          playbackTime={playbackTime}
          onDurationChange={onDurationChange}
          onError={onError}
          onReady={onReady}
          onTimeUpdate={onTimeUpdate}
        />
      </div>
    </div>
  );
}

function ModelPlaybackControl({
  disabled,
  duration,
  isPlaying,
  time,
  onSeek,
  onToggle,
}: {
  disabled: boolean;
  duration: number;
  isPlaying: boolean;
  time: number;
  onSeek: (time: number) => void;
  onToggle: () => void;
}) {
  return (
    <div className="model-playback-control">
      <button disabled={disabled} type="button" onClick={onToggle} aria-label={isPlaying ? "暂停模型" : "播放模型"}>
        {isPlaying ? (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M8 5v14" />
            <path d="M16 5v14" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M8 5.5v13l10-6.5-10-6.5Z" />
          </svg>
        )}
      </button>
      <input
        aria-label="模型播放进度"
        disabled={disabled || duration <= 0}
        max={duration || 1}
        min={0}
        step={0.01}
        type="range"
        value={clamp(time, 0, duration || 1)}
        onChange={(event) => onSeek(Number(event.target.value))}
      />
      <span>{formatShortTime(time)}</span>
    </div>
  );
}

function MotionChart({
  item,
  onChartChange,
  onFrameChange,
  options,
  selectedFrame,
}: {
  item: ChartItem | null;
  onChartChange: (key: string) => void;
  onFrameChange: (frame: number) => void;
  options: ChartItem[];
  selectedFrame: number;
}) {
  if (!item) {
    return <p className="result-note">暂无动作数据。</p>;
  }

  return (
    <div className="motion-chart-card">
      <div className="chart-toolbar">
        <select value={item.definition.key} onChange={(event) => onChartChange(event.target.value)} aria-label="选择数据指标">
          {options.map((option) => (
            <option key={option.definition.key} value={option.definition.key}>
              {option.definition.name}
            </option>
          ))}
        </select>
        <div className="chart-legend">
          <span><i className="student" />学生</span>
          <span><i className="standard" />标准</span>
        </div>
      </div>
      <DualLineChart item={item} selectedFrame={selectedFrame} />
      <div className="chart-frame-slider">
        <input
          aria-label="动作数据帧位置"
          max={Math.max(0, item.maxFrameCount - 1)}
          min={0}
          step={1}
          type="range"
          value={clamp(selectedFrame, 0, Math.max(0, item.maxFrameCount - 1))}
          onChange={(event) => onFrameChange(Number(event.target.value))}
        />
        <span>{selectedFrame}/{Math.max(0, item.maxFrameCount - 1)} 帧</span>
      </div>
    </div>
  );
}

function DualLineChart({ item, selectedFrame }: { item: ChartItem; selectedFrame: number }) {
  const width = 360;
  const height = 188;
  const padding = { bottom: 34, left: 38, right: 18, top: 18 };
  const values = [...item.seriesFirst, ...item.seriesSecond];
  const minValue = values.length ? Math.min(...values) : 0;
  const maxValue = values.length ? Math.max(...values) : 1;
  const range = maxValue - minValue || 1;
  const yMin = minValue - range * 0.18;
  const yMax = maxValue + range * 0.18;
  const yRange = yMax - yMin || 1;
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;

  function points(series: number[]) {
    return series.map((value, index) => {
      const x = padding.left + (index / Math.max(1, item.maxFrameCount - 1)) * plotWidth;
      const y = padding.top + (1 - (value - yMin) / yRange) * plotHeight;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(" ");
  }

  const currentX = padding.left + (clamp(selectedFrame, 0, item.maxFrameCount - 1) / Math.max(1, item.maxFrameCount - 1)) * plotWidth;
  const yTicks = [0, 0.5, 1].map((ratio) => yMin + (1 - ratio) * yRange);

  return (
    <svg className="motion-chart-svg" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`${item.definition.name} 对比曲线`}>
      <rect className="chart-plot-bg" x={padding.left} y={padding.top} width={plotWidth} height={plotHeight} rx="8" />
      {yTicks.map((_, index) => {
        const y = padding.top + index * (plotHeight / 2);
        return <line className="chart-grid-line" key={index} x1={padding.left} x2={width - padding.right} y1={y} y2={y} />;
      })}
      <line className="chart-axis-strong" x1={padding.left} x2={width - padding.right} y1={height - padding.bottom} y2={height - padding.bottom} />
      <line className="chart-axis-strong" x1={padding.left} x2={padding.left} y1={padding.top} y2={height - padding.bottom} />
      {item.seriesFirst.length > 1 ? <polyline className="chart-line student" fill="none" points={points(item.seriesFirst)} /> : null}
      {item.seriesSecond.length > 1 ? <polyline className="chart-line standard" fill="none" points={points(item.seriesSecond)} /> : null}
      <line className="chart-current-line" x1={currentX} x2={currentX} y1={padding.top} y2={height - padding.bottom} />
      <text className="chart-axis-label" x={padding.left} y={height - 8}>帧</text>
      <text className="chart-axis-label" x={8} y={padding.top + 10}>{item.definition.unit}</text>
      <text className="chart-tick" x={padding.left - 6} y={padding.top + 4} textAnchor="end">{yMax.toFixed(0)}</text>
      <text className="chart-tick" x={padding.left - 6} y={height - padding.bottom} textAnchor="end">{yMin.toFixed(0)}</text>
    </svg>
  );
}

function LlmReport({ analysis, llmStatus }: { analysis: TeacherWebLlmAnalysis | null; llmStatus: string }) {
  if (!analysis) {
    return (
      <div className="report-empty">
        <strong>{llmStatus === "error" ? "报告暂未取到" : "报告生成中"}</strong>
        <p>模型和数据会先展示，报告完成后本页会自动更新。</p>
      </div>
    );
  }

  return (
    <div className="report-stack">
      <ReportList title="整体总结" values={analysis.summary} />
      <FrameReport title="起始阶段" description={analysis.start_frame} />
      <FrameReport title="击球阶段" description={analysis.impact_frame} />
      <FrameReport title="结束阶段" description={analysis.end_frame} />
    </div>
  );
}

function FrameReport({ description, title }: { description?: TeacherWebQwenDescription | null; title: string }) {
  if (!description) return null;

  return (
    <section className="report-section">
      <h4>{title}</h4>
      <ReportList title="优点" values={description.strengths} />
      <ReportList title="问题" values={description.weaknesses} />
      <ReportList title="改进" values={description.improvements} />
      <ReportList title="训练建议" values={description.training_plan} />
    </section>
  );
}

function ReportList({ title, values }: { title: string; values?: string[] | null }) {
  if (!values || values.length === 0) return null;

  return (
    <div className="report-list">
      <span>{title}</span>
      <ul>
        {values.map((item, index) => (
          <li key={`${title}-${index}`}><MarkdownText text={item} /></li>
        ))}
      </ul>
    </div>
  );
}

function MarkdownText({ text }: { text: string }) {
  const parts: ReactNode[] = [];
  const regex = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(<strong key={`${match.index}-${match[1]}`}>{match[1]}</strong>);
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return <>{parts}</>;
}

function InfoDialog({ children, onClose, title }: { children: ReactNode; onClose: () => void; title: string }) {
  return (
    <div className="student-logout-dialog-backdrop" role="presentation" onClick={onClose}>
      <div
        aria-labelledby="result-info-title"
        aria-modal="true"
        className="student-logout-dialog result-info-dialog"
        role="dialog"
        onClick={(event) => event.stopPropagation()}
      >
        <h3 id="result-info-title">{title}</h3>
        <div>{children}</div>
        <div className="student-logout-actions">
          <button className="student-logout-primary" type="button" onClick={onClose}>
            知道了
          </button>
        </div>
      </div>
    </div>
  );
}

function ResultErrorCard({ message }: { message: string }) {
  return (
    <article className="result-card result-error-card">
      <strong>{message}</strong>
      <button className="ghost-button" type="button" onClick={() => window.history.back()}>
        返回上一页
      </button>
    </article>
  );
}

function buildChartItems(firstData: TeacherWebCompData | null, secondData: TeacherWebCompData | null, side: "left" | "right") {
  const firstStats = firstData?.frame_stats as Record<string, unknown> | undefined;
  const secondStats = secondData?.frame_stats as Record<string, unknown> | undefined;
  const preferred = chartDefinitions.filter((definition) => definition.key.includes(`_${side}`) || isCenterMetric(definition.key));
  const rest = chartDefinitions.filter((definition) => !preferred.includes(definition));

  return [...preferred, ...rest].map((definition) => {
    const seriesFirst = extractSeries(firstStats?.[definition.key], definition.key);
    const seriesSecond = extractSeries(secondStats?.[definition.key], definition.key);
    const maxFrameCount = Math.max(seriesFirst.length, seriesSecond.length);
    return { definition, maxFrameCount, seriesFirst, seriesSecond };
  }).filter((item) => item.maxFrameCount > 1);
}

function extractSeries(raw: unknown, key: string): number[] {
  if (!Array.isArray(raw)) return [];

  const toNumberSeries = (value: unknown): number[] => {
    if (!Array.isArray(value)) return [];
    return value.filter((item): item is number => typeof item === "number" && Number.isFinite(item));
  };

  const directSeries = toNumberSeries(raw);
  if (directSeries.length > 0) return directSeries;

  const originalSeries = toNumberSeries(raw[0]);
  if (originalSeries.length > 0) return originalSeries;

  if (key === "center_of_gravity") {
    return raw.flatMap((item) => {
      if (!Array.isArray(item)) return [];
      for (let index = item.length - 1; index >= 0; index -= 1) {
        const value = item[index];
        if (typeof value === "number" && Number.isFinite(value)) return [value];
      }
      return [];
    });
  }

  return [];
}

function preferredChartSide(techType?: string | null): "left" | "right" {
  return techType?.startsWith("left_") ? "left" : "right";
}

function isCenterMetric(key: string) {
  return key === "trunk_flexion_angle" || key === "trunk_lateral_tilt_angle" || key === "trunk_rotation_angle" || key === "center_of_gravity";
}

function frameFromProgress(time: number, duration: number, frameCount: number): number {
  if (duration <= 0 || frameCount <= 1) return 0;
  return clamp(Math.round((time / duration) * (frameCount - 1)), 0, frameCount - 1);
}

function getLastTeacherToken(): string | null {
  try {
    return window.localStorage.getItem(lastTeacherTokenKey);
  } catch {
    return null;
  }
}

function statusText(status?: string, error?: string): string {
  if (error) return "读取失败";
  switch (status) {
    case "comp completed":
      return "已完成";
    case "comp failed":
      return "分析失败";
    case "started":
      return "分析中";
    default:
      return "读取中";
  }
}

function statusTone(status?: string, error?: string): string {
  if (error || status === "comp failed") return "error";
  if (status === "comp completed") return "success";
  return "running";
}

function formatShortTime(time: number): string {
  if (!Number.isFinite(time) || time < 0) return "0:00";
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
